@if (isPurchasedOrdersDataLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  <div class="container-fluid">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-1 fw-bold">Purchase Orders</h5>
        <span class="sub-text">Manage supply chain and procurement</span>
      </div>

      <div class="d-flex flex-wrap gap-2">
        @if (hasActiveFilters) {
          <button class="btn btn-secondary d-flex align-items-center gap-1" (click)="clearFilters()">
            <i class="bi bi-x-circle"></i>
            Clear
          </button>
        }

        <button
          class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
          (click)="exportPurchaseOrders()"
          [disabled]="isExporting">
          @if (isExporting) {
            <span class="spinner-border spinner-border-sm"></span>
          } @else {
            <i class="bi bi-download"></i>
          }
          Export
        </button>

        <button class="btn btn-primary btn-enjochem" (click)="onCreatePurchaseOrder()">
          <i class="bi bi-plus-circle"></i>
          <span>Create Purchase Order</span>
        </button>
      </div>
    </div>

    <div class="row g-2 mb-4">
      <div class="col-12 col-md-4">
        <ng-select
          placeholder="All Suppliers"
          [items]="supplierOptions"
          [(ngModel)]="selectedSupplier"
          bindValue="id"
          bindLabel="name"
          [clearable]="true"
          [searchable]="true"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-12 col-md-4">
        <ng-select
          placeholder="All Places of Delivery"
          [items]="wareHouses"
          [(ngModel)]="selectedWarehouse"
          bindValue="id"
          bindLabel="name"
          [clearable]="true"
          [searchable]="true"
          [disabled]="currentUser?.role !== UserRole.ADMIN"
          (ngModelChange)="onWareHouseSelected(selectedWarehouse!)" />
      </div>

      <div class="col-12 col-md-4">
        <ng-select
          placeholder="All Companies"
          [items]="companies"
          [(ngModel)]="selectedCompany"
          bindValue="id"
          bindLabel="name"
          [clearable]="true"
          [searchable]="true"
          (ngModelChange)="onFilterChange()" />
      </div>
    </div>

    <div class="table-responsive">
      @if (purchasedOrders.length > 0) {
        <app-table
          [columns]="purchasedOrderTableColumns"
          [data]="purchasedOrders"
          [actions]="[Action.VIEW, Action.EDIT, Action.DOWNLOAD, Action.DELETE]"
          (onView)="handlePurchasedOrderView($event)"
          (onDelete)="handlePurchasedOrderDelete($event)"
          (onDownload)="handlePurchasedOrderDownload($event)"
          (onEdit)="handlePurchasedOrderEdit($event)"
          (onClickAddEntry)="prefillRawMaterialEntryFromPO($event)" />
      } @else {
        <div class="empty-state-container">
          <i class="bi bi-cart"></i>
          @if (hasActiveFilters) {
            <h5>No Matching Purchase Orders</h5>
            <p>No purchase order matches the current filters.</p>
            <button class="btn btn-outline-primary" (click)="clearFilters()">Clear filters</button>
          } @else {
            <h5>No Purchase Orders</h5>
            <p>You haven't created any purchase orders yet.</p>
          }
        </div>
      }
    </div>

    @if (purchasedOrders.length > 0) {
      <div class="mt-4">
        <app-pagination
          [currentPage]="currentPage"
          [totalCount]="totalCount"
          [pageSize]="10"
          [hasNextPage]="hasNextPage"
          [hasPreviousPage]="hasPreviousPage"
          (pageChange)="onPageChange($event)"></app-pagination>
      </div>
    }
  </div>
}

<!-- Create / Edit Purchase Order Modal -->
<ng-template #createPurchaseOrderContent let-modal>
  <div class="modal-content border-0">
    <div class="modal-header">
      <h5 class="modal-title">
        {{ mode === 'edit' ? 'Edit Purchase Order' : 'Create Purchase Order' }}
      </h5>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>

    <div class="modal-body">
      <app-error-alert [error]="purchaseOrderError" />

      <form [formGroup]="purchaseOrderForm">
        <!-- Supplier & Order Details -->
        <p class="fw-semibold text-muted small text-uppercase mb-2">Order Details</p>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">
              Date
              <span class="text-danger">*</span>
            </label>
            <input
              type="date"
              class="form-control"
              formControlName="date"
              [class.is-invalid]="purchaseOrderForm.get('date')?.invalid && purchaseOrderForm.get('date')?.touched" />
            <div class="invalid-feedback">Date is required.</div>
          </div>

          <div class="col-md-4">
            <label class="form-label">Company</label>
            <ng-select
              placeholder="Select Company"
              [items]="companies"
              formControlName="company_id"
              bindValue="id"
              bindLabel="name"
              [clearable]="true"
              [searchable]="false" />
          </div>

          <div class="col-md-4">
            <label class="form-label">
              Supplier
              <span class="text-danger">*</span>
            </label>
            <ng-select
              placeholder="Select Supplier"
              [items]="suppliers"
              formControlName="supplier_id"
              bindValue="id"
              bindLabel="name"
              [clearable]="false"
              [searchable]="true" />
            @if (purchaseOrderForm.get('supplier_id')?.invalid && purchaseOrderForm.get('supplier_id')?.touched) {
              <div class="text-danger small mt-1">Supplier is required.</div>
            }
          </div>

          <div class="col-md-4">
            <label class="form-label">
              Place of Delivery
              <span class="text-danger">*</span>
            </label>
            <ng-select
              placeholder="Select Delivery Location"
              [items]="wareHouses"
              formControlName="place_of_delivery"
              bindValue="id"
              bindLabel="name"
              [clearable]="false"
              [searchable]="false" />
            @if (
              purchaseOrderForm.get('place_of_delivery')?.invalid && purchaseOrderForm.get('place_of_delivery')?.touched
            ) {
              <div class="text-danger small mt-1">Place of delivery is required.</div>
            }
          </div>
        </div>

        <!-- Supplier Info (read-only, auto-filled) -->
        <p class="fw-semibold text-muted small text-uppercase mt-4 mb-2">Supplier Info</p>
        <div class="row g-3">
          <div class="col-md-8">
            <label class="form-label">Address</label>
            <input type="text" class="form-control" formControlName="address" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Email</label>
            <input type="text" class="form-control" formControlName="email" />
          </div>
          <div class="col-md-4">
            <label class="form-label">GST</label>
            <input type="text" class="form-control" formControlName="gst_number" />
          </div>
          <div class="col-md-4">
            <label class="form-label">PAN</label>
            <input type="text" class="form-control" formControlName="pan_card_number" />
          </div>
          <div class="col-md-4">
            <label class="form-label">UDYAM</label>
            <input type="text" class="form-control" formControlName="udyam_number" />
          </div>
        </div>

        <hr class="my-4" />

        <!-- Items -->
        <p class="fw-semibold text-muted small text-uppercase mb-2">Raw Materials</p>
        <div formArrayName="items" class="d-flex flex-column gap-3">
          <div
            *ngFor="let item of items.controls; let i = index"
            [formGroupName]="i"
            class="border rounded-3 p-3 bg-light">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">
                  Raw Material
                  <span class="text-danger">*</span>
                </label>
                <ng-select
                  placeholder="Select Raw Material"
                  [items]="rawMaterials"
                  formControlName="raw_material"
                  bindValue="id"
                  bindLabel="name"
                  [clearable]="false"
                  [searchable]="true"
                  (change)="onRawMaterialChange(i)">
                  <ng-template ng-label-tmp let-item="item">
                    <span class="text-muted font-monospace me-1">{{ item.category_short_code }}</span>
                    &ndash; {{ item.name }}
                  </ng-template>
                  <ng-template ng-option-tmp let-item="item">
                    <span class="text-muted font-monospace me-1">{{ item.category_short_code }}</span>
                    &ndash; {{ item.name }}
                  </ng-template>
                </ng-select>
                @if (item.get('raw_material')?.invalid && item.get('raw_material')?.touched) {
                  <div class="text-danger small mt-1">Raw material is required.</div>
                }
              </div>

              <div class="col-md-2">
                <label class="form-label">Unit</label>
                <input type="text" class="form-control" formControlName="unit" readonly />
              </div>

              <div class="col-md-2">
                <label class="form-label">Qty</label>
                <input type="number" class="form-control" formControlName="quantity" />
              </div>

              <div class="col-md-2">
                <div class="d-flex align-items-center gap-2">
                  <label class="form-label mb-0">GST %</label>
                  @if (isGstOverridden(item)) {
                    <span
                      class="small fw-semibold px-2 py-0"
                      title="Differs from the material's default rate"
                      style="background-color: #fff3cd; color: #856404; border: 1px solid #ffc107; border-radius: 4px">
                      Overridden
                    </span>
                  }
                </div>
                <input
                  type="number"
                  class="form-control mt-1"
                  formControlName="gst_percentage"
                  [class.is-invalid]="item.get('gst_percentage')?.invalid && item.get('gst_percentage')?.touched"
                  placeholder="—"
                  step="0.01"
                  min="0"
                  max="100"
                  (input)="onGstPercentageChange(i)" />
                @if (item.get('gst_percentage')?.invalid && item.get('gst_percentage')?.touched) {
                  <div class="text-danger small mt-1">Enter 0–100 (max 2 decimals).</div>
                }
              </div>

              <div class="col-md-3">
                <label class="form-label">Rate (Excl. GST)</label>
                <input
                  type="number"
                  class="form-control"
                  formControlName="rate_excl_gst"
                  placeholder="—"
                  step="0.01"
                  min="0"
                  (input)="onRateExclGstInput(i)" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Rate (Incl. GST)</label>
                <input
                  type="number"
                  class="form-control"
                  formControlName="rate_incl_gst"
                  placeholder="—"
                  step="0.01"
                  min="0"
                  (input)="onRateInclGstInput(i)" />
              </div>

              <div class="col-md-6">
                <label class="form-label text-muted small">Amount (Incl. GST)</label>
                <div class="form-control bg-white fw-semibold">{{ formatAmount(getLineAmount(item)) }}</div>
              </div>
            </div>

            <div class="text-end mt-2">
              <button
                *ngIf="items.length > 1"
                type="button"
                class="btn btn-sm btn-outline-danger"
                (click)="removeItem(i)">
                <i class="bi bi-trash me-1"></i>
                Remove
              </button>
            </div>
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
          <button class="btn btn-sm btn-outline-primary" type="button" (click)="addItem()">
            <i class="bi bi-plus-circle me-1"></i>
            Add Product
          </button>

          <div class="text-end">
            <div class="text-muted small">Estimated Total (Incl. GST)</div>
            <div class="fw-bold">{{ formatAmount(estimatedOrderTotal) }}</div>
            <div class="text-muted" style="font-size: 0.7rem">Final total is calculated on save.</div>
          </div>
        </div>
      </form>
    </div>

    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button
        class="btn btn-primary btn-enjochem"
        [disabled]="isPurchasedOrderDataSaving"
        (click)="savePurchaseOrder()">
        @if (isPurchasedOrderDataSaving) {
          <app-loading-spinner [inline]="true"></app-loading-spinner>
        }
        Save Order
      </button>
    </div>
  </div>
</ng-template>

<!-- Add / Update Raw Material Entry Modal -->
<ng-template #createRawMaterialEntryContent let-modal>
  <div class="modal-content border-0">
    <div class="modal-header">
      <div>
        <h5 class="modal-title mb-0">
          {{ isEditMode ? 'Update Raw Material Entry' : 'Add Raw Material Entry' }}
        </h5>
        <span class="badge mt-1" [class.bg-warning]="!isEditMode" [class.bg-primary]="isEditMode">
          {{ isEditMode ? 'Editing existing entries' : 'Recording new receipt' }}
        </span>
      </div>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>

    <div class="modal-body">
      <app-error-alert [error]="rawMaterialEntryError" />

      <form [formGroup]="rawMaterialEntryForm">
        <!-- PO Summary (read-only) -->
        <p class="fw-semibold text-muted small text-uppercase mb-2">Purchase Order Info</p>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">PO Number</label>
            <input type="text" class="form-control" formControlName="po_number" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Supplier</label>
            <input type="text" class="form-control" formControlName="supplier_name" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Supplier GST No.</label>
            <input type="text" class="form-control" formControlName="supplier_gst" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Company</label>
            <input type="text" class="form-control" formControlName="company_name" placeholder="—" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Date of Order</label>
            <input type="date" class="form-control" formControlName="date_of_order" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Date of Receipt</label>
            <input type="date" class="form-control" formControlName="date_of_receipt" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Total Cost (Incl. GST)</label>
            <div class="form-control bg-light fw-semibold">{{ formatAmount(entryPurchaseOrder?.total_cost) }}</div>
          </div>
        </div>

        <hr class="my-4" />

        <!-- Per-item entries -->
        <p class="fw-semibold text-muted small text-uppercase mb-2">Material Entries</p>
        <div formArrayName="items" class="d-flex flex-column gap-3">
          <div *ngFor="let item of poItems.controls; let i = index" [formGroupName]="i" class="border rounded-3">
            <!-- Card header: material identity (read-only) -->
            <div
              class="d-flex align-items-center gap-3 px-3 py-2 bg-light border-bottom"
              style="border-radius: 0.5rem 0.5rem 0 0">
              <span class="fw-semibold">{{ item.get('material_name')?.value }}</span>
              <span class="badge bg-secondary">{{ item.get('material_code')?.value }}</span>
              <span class="text-muted small ms-auto">{{ item.get('uom')?.value }}</span>
            </div>

            <div class="p-3">
              <!-- Primary action: ordered vs received side by side -->
              <div class="row g-3 mb-3">
                <div class="col-md-3">
                  <label class="form-label text-muted small">Qty Ordered</label>
                  <div class="form-control bg-light text-muted">
                    {{ item.get('quantity_ordered')?.value }}
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-semibold">
                    Qty Received
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    class="form-control"
                    formControlName="quantity_received"
                    [class.is-invalid]="
                      item.get('quantity_received')?.invalid && item.get('quantity_received')?.touched
                    "
                    placeholder="Enter qty" />
                  <div class="invalid-feedback">Required, minimum 1.</div>
                </div>
                <div class="col-md-3">
                  <label class="form-label text-muted small">Rate (Excl. GST)</label>
                  <div class="form-control bg-light text-muted">
                    {{ item.get('rate_excl_gst')?.value != null ? item.get('rate_excl_gst')?.value : '—' }}
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label text-muted small">Rate (Incl. GST)</label>
                  <div class="form-control bg-light text-muted">
                    {{ item.get('rate_incl_gst')?.value != null ? item.get('rate_incl_gst')?.value : '—' }}
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <label class="form-label text-muted small mb-0">GST %</label>
                    @if (isGstOverridden(item)) {
                      <span
                        class="small fw-semibold px-2 py-0"
                        [title]="'Material default: ' + gstDisplay(item.get('raw_material_gst_percentage')?.value)"
                        style="
                          background-color: #fff3cd;
                          color: #856404;
                          border: 1px solid #ffc107;
                          border-radius: 4px;
                        ">
                        Overridden
                      </span>
                    }
                  </div>
                  <div class="form-control bg-light text-muted">
                    {{ gstDisplay(item.get('gst_percentage')?.value) }}
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label text-muted small">Amount (Incl. GST)</label>
                  <div class="form-control bg-light text-muted">
                    {{ formatAmount(item.get('amount_incl_gst')?.value) }}
                  </div>
                </div>
                <div class="col-md-3">
                  <label class="form-label text-muted small">Batch No.</label>
                  <input type="text" class="form-control" formControlName="batch_no" placeholder="e.g. B-2026-001" />
                </div>
                <div class="col-md-3">
                  <label class="form-label text-muted small">QC Status</label>
                  <ng-select
                    placeholder="— Select —"
                    [items]="qcStatusOptions"
                    formControlName="qc_status"
                    bindValue="value"
                    bindLabel="label"
                    [clearable]="true"
                    [searchable]="false" />
                </div>
                <div class="col-12">
                  <label class="form-label text-muted small">QC Remarks</label>
                  <input type="text" class="form-control" formControlName="qc_remarks" placeholder="Optional remarks" />
                </div>
              </div>

              <!-- Warehouse per entry -->
              <div class="d-flex justify-content-end pt-2 border-top">
                <div>
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <label class="form-label text-muted small mb-0">Warehouse</label>
                    @if (
                      item.get('warehouse_id')?.value &&
                      item.get('po_warehouse_id')?.value &&
                      item.get('warehouse_id')?.value !== item.get('po_warehouse_id')?.value
                    ) {
                      <span
                        class="small fw-semibold px-2 py-0"
                        style="
                          text-transform: none;
                          background-color: #fff3cd;
                          color: #856404;
                          border: 1px solid #ffc107;
                          border-radius: 4px;
                        ">
                        Different warehouse
                      </span>
                    }
                  </div>
                  <ng-select
                    placeholder="Select warehouse"
                    [items]="wareHouses"
                    formControlName="warehouse_id"
                    bindValue="id"
                    bindLabel="name"
                    [clearable]="false"
                    [searchable]="false"
                    style="min-width: 200px" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-4" />

        <!-- Invoice & Payment Details -->
        <p class="fw-semibold text-muted small text-uppercase mb-2">Invoice & Payment</p>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Invoice Number</label>
            <input type="text" class="form-control" formControlName="invoice_number" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Invoice Date</label>
            <input type="date" class="form-control" formControlName="invoice_date" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Payment Terms</label>
            <input type="text" class="form-control" formControlName="payment_terms" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Payment Status</label>
            <ng-select
              placeholder="Select status"
              [items]="paymentStatusOptions"
              formControlName="payment_status"
              bindValue="value"
              bindLabel="label"
              [clearable]="true"
              [searchable]="false" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Approval Status</label>
            <ng-select
              placeholder="Select status"
              [items]="approvalStatusOptions"
              formControlName="approval_status"
              bindValue="value"
              bindLabel="label"
              [clearable]="true"
              [searchable]="false" />
            <div class="form-text text-muted">
              <i class="bi bi-info-circle me-1"></i>
              For record-keeping only. Stock is added immediately when the entry is saved — approval is not required.
            </div>
          </div>
          <div class="col-md-4">
            <label class="form-label">Upload Document</label>
            <input type="file" class="form-control" (change)="onDocumentUpload($event)" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Created By</label>
            <input type="text" class="form-control" formControlName="created_by" />
          </div>
        </div>
      </form>
    </div>

    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button
        class="btn btn-primary btn-enjochem"
        [disabled]="isUpdatingPurchasedOrderData"
        (click)="saveRawMaterialEntry()">
        @if (isUpdatingPurchasedOrderData) {
          <app-loading-spinner [inline]="true"></app-loading-spinner>
        }
        {{ isEditMode ? 'Update Entry' : 'Save Entry' }}
      </button>
    </div>
  </div>
</ng-template>

<!-- Purchase Order Details — read-only, opened straight from the list -->
<ng-template #purchaseOrderDetailsContent>
  @if (viewedPurchaseOrder; as po) {
    <div class="modal-content border-0">
      <div class="modal-header">
        <div>
          <h5 class="modal-title mb-1">Purchase Order {{ po.po_number }}</h5>
          <div class="sub-text small">Ordered {{ po.date_of_order | date: 'dd MMM yyyy' }}</div>
        </div>
        <button type="button" class="btn-close" aria-label="Close" (click)="closeDetails()"></button>
      </div>

      <div class="modal-body p-4">
        <div class="d-flex flex-wrap gap-2 mb-4">
          <span class="app-badge" [class.badge-success]="po.is_approved" [class.badge-warning]="!po.is_approved">
            {{ po.is_approved ? 'Approved' : 'Approval pending' }}
          </span>
          @if (po.payment_status) {
            <span
              class="app-badge"
              [class.badge-success]="po.payment_status === 'PAID'"
              [class.badge-warning]="po.payment_status !== 'PAID'">
              Payment {{ po.payment_status | lowercase }}
            </span>
          }
          @if (po.entries?.length) {
            <span class="app-badge badge-secondary">Received</span>
          }
        </div>

        <!-- Who, where, from whom -->
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <span class="detail-label">Supplier</span>
            <div class="fw-semibold">{{ po.supplier_name || '-' }}</div>
          </div>
          <div class="col-md-4">
            <span class="detail-label">Place of Delivery</span>
            <div class="fw-semibold">{{ po.warehouse_name || '-' }}</div>
          </div>
          <div class="col-md-4">
            <span class="detail-label">Company</span>
            <div class="fw-semibold">{{ po.company_name || '-' }}</div>
          </div>
          <div class="col-md-4">
            <span class="detail-label">Created By</span>
            <div>{{ po.created_by_name || '-' }}</div>
          </div>
          <div class="col-md-4">
            <span class="detail-label">Date of Receipt</span>
            <div>{{ po.date_of_receipt ? (po.date_of_receipt | date: 'dd MMM yyyy') : '-' }}</div>
          </div>
          <div class="col-md-4">
            <span class="detail-label">Payment Terms</span>
            <div>{{ po.payment_terms || '-' }}</div>
          </div>
        </div>

        <!-- Line items -->
        <h6 class="fw-bold mb-2">Materials</h6>
        <div class="table-responsive border bg-white mb-3">
          <table class="table align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th class="ps-3">Material</th>
                <th class="text-end">Ordered</th>
                <th class="text-end">Received</th>
                <th>Unit</th>
                <th class="text-end">Rate excl GST</th>
                <th class="text-end">GST</th>
                <th class="text-end">Rate incl GST</th>
                <th class="text-end pe-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              @for (item of po.raw_materials; track item.id) {
                <tr>
                  <td class="ps-3 fw-semibold">{{ item.raw_material_name }}</td>
                  <td class="text-end">{{ item.quantity_ordered }}</td>
                  <td class="text-end">
                    @if (receivedQuantity(po, item) !== null) {
                      {{ receivedQuantity(po, item) }}
                    } @else {
                      <span class="text-muted">-</span>
                    }
                  </td>
                  <td>{{ item.unit_short_name || item.unit_name }}</td>
                  <td class="text-end">{{ formatAmount(item.rate_excl_gst) }}</td>
                  <td class="text-end">{{ gstDisplay(item.gst_percentage) }}</td>
                  <td class="text-end">{{ formatAmount(item.rate_incl_gst) }}</td>
                  <td class="text-end pe-3">{{ formatAmount(item.amount_incl_gst) }}</td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="8" class="text-center text-muted py-4">No materials on this order.</td>
                </tr>
              }
            </tbody>
            <tfoot class="table-light">
              <tr>
                <td colspan="7" class="text-end fw-semibold">Total</td>
                <td class="text-end pe-3 fw-bold">{{ formatAmount(po.total_cost) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Invoice, only once one exists -->
        @if (po.invoice_number || po.invoice_date) {
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <span class="detail-label">Invoice Number</span>
              <div class="fw-semibold">{{ po.invoice_number || '-' }}</div>
            </div>
            <div class="col-md-4">
              <span class="detail-label">Invoice Date</span>
              <div>{{ po.invoice_date ? (po.invoice_date | date: 'dd MMM yyyy') : '-' }}</div>
            </div>
          </div>
        }

        @if (po.remarks) {
          <div class="mb-3">
            <span class="detail-label">Remarks</span>
            <div>{{ po.remarks }}</div>
          </div>
        }

        @if (po.po_doc) {
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-paperclip text-muted"></i>
            <a [href]="po.po_doc.file" target="_blank" rel="noopener">{{ po.po_doc.filename }}</a>
          </div>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDetails()">
          <i class="bi bi-x-circle"></i>
          <span>Close</span>
        </button>
        <button type="button" class="btn btn-primary btn-enjochem" (click)="editViewedPurchaseOrder()">
          <i class="bi bi-pencil"></i>
          <span>Edit</span>
        </button>
      </div>
    </div>
  }
</ng-template>
